import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { compose, bindActionCreators } from 'redux';
import _ from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';

import { captureException } from '@playbookux/playbook-web-core';
import { fetchCompany } from '../../../../core/actions';
import CompanyMembers from '../company-members';
import CompanyInvites from '../company-invites';
import schema from '../schema';
import { config } from '../../../../config';

import {
  UPDATE_COMPANY_TOGGLE,
  STATUS,
  CREATE_INVITE_LINK,
  updateCompanyInviteCreated,
  CREATE_COMPANY_INVITES,
  GET_COMPANY_INVITES,
  GET_CURRENT_COMPANY_MEMBER,
  RESEND_INVITES,
} from '../graphql';

import {
  Button,
  Container,
  ContainerRow,
  Description,
  DescriptionSecondary,
  ErrorMessage,
  Input,
  Link,
  Loader,
  Message,
  MessageContainer,
  MessageContainerBelow,
  Section,
  Subtitle,
  SuccessMessage,
  Title,
  Toggle,
  ToggleWrapper,
  ContainerLoading,
  ContainerEmailInvite,
  Search,
  SearchContainer,
} from './styles';

const MS_TIMER = 5000;
const ADMIN_ROLE = 'admin';
const INVITING_SUCCESS_MESSAGE = 'Invitation Sent!';
const INVITING_ERROR_MESSAGE = 'Error while inviting team member.';
const COMPANY_MEMBERS_INVITATIONS_ERROR = 'Error while retrieving company members/invitations.';
const LEARN_MORE_LINK = 'http://help.playbookux.com/en/articles/5535252-inviting-with-a-link-team-members-with-a-link';
const MAX_INVITES_ERROR_MESSAGE = 'You have reached the maximum number of invites. To send more, existing invites must be accepted.'
  + '\nFor additional team member invites, contact hello@playbookux.com.';

const Team = (props) => {
  const {
    company,
    meId,
    onFetchCompany,
  } = props;

  const [invitationSent, setInvitationSent] = useState(false);
  const [errorsMsg, setErrorMsg] = useState('');
  const [toggleValue, setToggleValue] = useState(false);
  const [hasInviteWithLinkError, setHasInviteWIthLinkError] = useState(false);
  const [emailsError, setEmailsError] = useState(false);
  const [emails, setEmails] = useState('');
  const [timer, setTimer] = useState(null);
  const [loadingToggle, setLoadingToggle] = useState(null);
  const [emailsAlreadyInvited, setEmailsAlreadyInvited] = useState([]);
  const [companyInvites, setCompanyInvites] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredTeamMembers, setFilteredTeamMembers] = useState({});

  const [updateCompanyToggle] = useMutation(UPDATE_COMPANY_TOGGLE);
  const [createInviteLink] = useMutation(CREATE_INVITE_LINK);

  const [
    createCompanyInvites,
    { error: emailInviteError, loading: emailInviteLoading },
  ] = useMutation(CREATE_COMPANY_INVITES, {
    onCompleted: (data) => {
      setCompanyInvites(data?.createCompanyInvites?.companyInvites || []);
      setEmailsAlreadyInvited(data?.createCompanyInvites?.emailsAlreadyInvited || []);
    },
    update: updateCompanyInviteCreated(company.id),
  });

  const [
    resendInvite,
    { error: resendEmailInviteError, loading: resendEmailInviteLoading },
  ] = useMutation(RESEND_INVITES, {
    onCompleted: (data) => {
      setCompanyInvites(data?.resendInvites?.companyInvites || []);
    },
    onError: (data) => {
      captureException('RESEND_INVITE_ERROR', data);
    },
  });

  const { data = {}, loading, error } = useQuery(GET_COMPANY_INVITES, {
    variables: {
      companyId: company.id,
      status: STATUS,
    },
    skip: !company.id,
  });

  const { data: currentData = {} } = useQuery(GET_CURRENT_COMPANY_MEMBER, {
    variables: {
      userId: meId,
      companyId: company.id,
    },
    skip: !meId || !company.id,
  });

  const { companyMembers } = currentData;

  const currentUser = _.first(companyMembers);

  const inviteLink = `${config.REACT_APP_PLATFORM_URI}/invite/${company?.inviteLink}`;

  useEffect(() => {
    if (company) {
      setToggleValue(!!company.inviteToggleUserId);
    }
  }, [company]);

  const handleMembersSubmit = useCallback(async () => {
    const newEmails = _.chain(emails)
      .split(',')
      .map(_.trim)
      .map(_.toLower)
      .value();

    const isValid = await schema.isValid({ emails: newEmails });

    if (!isValid) {
      setEmailsError(true);
      return;
    }

    setEmailsError(false);

    try {
      await createCompanyInvites({
        variables: {
          companyId: company.id,
          emails: newEmails,
        },
      });

      setEmails('');
      setInvitationSent(true);
    } catch (ex) {
      captureException('HANDLE_MEMBERS_SUBMIT_FAILED', ex);
      setEmailsError(true);
      if (ex.message === 'Cannot send invite more than once a week') {
        setErrorMsg('This team member has already been invited to PlaybookUX.');
      } else {
        setErrorMsg(MAX_INVITES_ERROR_MESSAGE);
      }
    }

    if (timer) {
      clearTimeout(timer);
    }

    const newTimer = setTimeout(() => {
      setInvitationSent(false);
    }, MS_TIMER);

    setTimer(newTimer);
  }, [emails, setEmails, setTimer, setInvitationSent, setEmailsError, createCompanyInvites]);

  const handleMembersChange = (event, { value }) => {
    setEmails(value);
    setEmailsError(false);
  };

  const handleUpdateInviteToggle = async (toggle) => {
    try {
      await updateCompanyToggle({
        variables: {
          companyId: company.id,
          toggleValue: toggle,
        },
      });
      setHasInviteWIthLinkError(false);
    } catch (err) {
      setHasInviteWIthLinkError(true);
      captureException('HANDLE_UPDATE_INVITE_TOGGLE_FAILED', err);
    }
  };

  const handleCreateInviteLink = async () => {
    try {
      await createInviteLink({
        variables: {
          companyId: company.id,
        },
      });
      setHasInviteWIthLinkError(false);
    } catch (err) {
      setHasInviteWIthLinkError(true);
      captureException('HANDLE_CREATE_INVITE_LINK_FAILED', err);
    }
  };

  const handleToggle = async () => {
    setLoadingToggle(true);
    if (!company.inviteLink) {
      // generate link for this company
      await handleCreateInviteLink();
      await onFetchCompany();
    }

    await handleUpdateInviteToggle(!toggleValue);
    setToggleValue(!toggleValue);
    setLoadingToggle(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
  };

  const handleResendInvite = async (email) => {
    try {
      await resendInvite({
        variables: {
          companyId: company.id,
          emails: [email],
        },
      });
    } catch (ex) {
      captureException('HANDLE_RESEND_INVITE_FAILED', ex);
    }
  };

  const filteredByEmail = useMemo(() => {
    if (data) {
      const emailInvites = _.filter(data.companyInvites, (invite) => invite.inviteType === 'email');
      return {
        companyInvites: emailInvites,
        companyMembers: data.companyMembers,
      };
    }
    return {};
  }, [data]);

  const filteredByLink = useMemo(() => {
    if (data) {
      const emailInvites = _.filter(data.companyInvites, (invite) => invite.inviteType === 'link' && invite.status === 'pending');
      return {
        companyInvites: emailInvites,
      };
    }
    return {};
  }, [data]);

  const filterTeamMembers = (searchValue) => {
    const formatSearchValue = (value) => _.trim(value).toLowerCase().replace(/\s+/g, '');

    const filterByEmail = (items, searchEmail) => _.filter(items, (item) => (
      _.includes(_.toLower(item.email), searchEmail)
    ));

    const filterByMembers = (members, searchMembers) => _.filter(members, (item) => {
      const { email = '' } = item.user;
      const { firstName = '', lastName = '' } = item.user?.profile;
      const fullName = `${firstName}${lastName}`.replace(/\s+/g, '');
      return _.includes(_.toLower(email), searchMembers) || _.includes(_.toLower(fullName), searchMembers);
    });

    const searchFormatted = formatSearchValue(searchValue);

    if (searchFormatted) {
      const invites = filterByEmail(filteredByEmail.companyInvites, searchFormatted);
      const members = filterByMembers(filteredByEmail.companyMembers, searchFormatted);
      setFilteredTeamMembers({
        companyInvites: invites,
        companyMembers: members,
      });
    } else {
      const emailInvites = _.filter(data.companyInvites, (invite) => invite.inviteType === 'email');
      setFilteredTeamMembers({
        companyInvites: emailInvites,
        companyMembers: data.companyMembers,
      });
    }
  };

  const debouncedMembersFilter = useCallback(_.debounce(filterTeamMembers, 200), [filteredByEmail]);

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearch(value);
    debouncedMembersFilter(value);
  };

  useEffect(() => {
    filterTeamMembers('');
  }, [filteredByEmail]);

  return (
    <Container>
      <ContainerLoading>
        {loadingToggle && <Loader data-loader="team-loader" />}
      </ContainerLoading>

      <Section>
        <Title as="h2">Invite your team</Title>

        <Subtitle as="h4">Invite via email</Subtitle>

        <ContainerEmailInvite>
          <Input
            error={emailsError}
            onChange={handleMembersChange}
            placeholder="Enter email addresses, separated by a comma"
            value={emails}
          />

          <Button
            disabled={emailInviteLoading || resendEmailInviteLoading}
            loading={emailInviteLoading || resendEmailInviteLoading}
            onClick={handleMembersSubmit}
          >
            Invite
          </Button>

          {emailInviteError && (
            <MessageContainer>
              <ErrorMessage bold>
                {errorsMsg || INVITING_ERROR_MESSAGE}
              </ErrorMessage>
            </MessageContainer>
          )}

          {invitationSent && companyInvites.length > 0 && (
            <MessageContainer>
              <SuccessMessage>{INVITING_SUCCESS_MESSAGE}</SuccessMessage>
            </MessageContainer>
          )}

          {resendEmailInviteError && (
            <MessageContainer>
              <ErrorMessage bold>
                {errorsMsg || INVITING_ERROR_MESSAGE}
              </ErrorMessage>
            </MessageContainer>
          )}

        </ContainerEmailInvite>

        {
          companyInvites.length > 0 && (
            <MessageContainerBelow>
              <SuccessMessage>
                {companyInvites.length}
                {' '}
                invites sent.
              </SuccessMessage>
            </MessageContainerBelow>
          )
        }
        {
          emailsAlreadyInvited.length > 0 && (
            <MessageContainerBelow>
              {
                emailsAlreadyInvited.map((email) => (
                  <ErrorMessage bold>
                    {email}
                    {' '}
                    already invited.
                  </ErrorMessage>
                ))
              }
            </MessageContainerBelow>
          )
        }
      </Section>

      <Section>
        <ContainerRow>
          <Subtitle as="h4">Invite with a link</Subtitle>
          <ToggleWrapper>
            <Toggle
              checked={toggleValue}
              onChange={handleToggle}
              id="ToggleComponent"
            />
          </ToggleWrapper>
        </ContainerRow>

        {hasInviteWithLinkError && (
          <ErrorMessage>
            Error something went wrong!
          </ErrorMessage>
        )}

        {toggleValue && (
          <>
            <DescriptionSecondary>
              Share this link with team members. For enhanced security,
              the link will be disabled and reset in 24 hours
            </DescriptionSecondary>

            <ContainerRow hasMarginBottom>
              <Input
                readOnly
                value={inviteLink}
              />

              <Button
                onClick={handleCopyLink}
              >
                Copy
              </Button>
            </ContainerRow>

          </>
        )}
      </Section>

      {toggleValue && currentUser && currentUser.role === ADMIN_ROLE && (
        <Section>
          <Subtitle as="h4">Invite with a link approvals</Subtitle>
          <Description>
            People who have signed up with a link
            will not have access to the platform until an admin approves them.
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href={LEARN_MORE_LINK}
            >
              Learn more
            </Link>
          </Description>

          <CompanyInvites
            data={filteredByLink}
            companyId={company.id}
          />

        </Section>
      )}

      <Section overflow="auto">
        <Title as="h2">Team Members</Title>

        <SearchContainer>
          <Search
            placeholder="Search email address, name"
            input={{
              icon: 'search',
              iconPosition: 'left',
            }}
            loading={false}
            onSearchChange={handleSearchChange}
            value={search}
            open={false}
            width="390px"
          />
        </SearchContainer>

        {loading && <Loader data-loader="team-members-loader" />}

        {error && (
          <Message>{COMPANY_MEMBERS_INVITATIONS_ERROR}</Message>
        )}

        {!_.isEmpty(data) && (
          <CompanyMembers
            companyId={company.id}
            data={filteredTeamMembers}
            meRole={currentUser?.role}
            meId={meId}
            resendInvite={handleResendInvite}
          />
        )}
      </Section>
    </Container>
  );
};

Team.propTypes = {
  company: PropTypes.shape({
    id: PropTypes.string,
    inviteLink: PropTypes.string,
    inviteToggleUserId: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  meId: PropTypes.string.isRequired,
  onFetchCompany: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  onFetchCompany: fetchCompany,
}, dispatch);

const mapStateToProps = ({ authentication }) => ({
  company: authentication.company,
  meId: authentication.profile.user_id,
});

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(Team);
